import { createSelector } from 'reselect';
import { PlannedTwinID } from '@ynomia/core/dist/blueprint';
import omit from 'lodash/omit';
import {
  getAssetManagerFilters,
  getAssetManagerSelectedAssetTypeId,
  getFilteredAssets,
  getFilteredAssetsContext,
  getPlanConfigurationsKeyedById,
  getPlannedTwinIDsKeyedByTwinId,
} from '..';
import { Asset } from '../../config/types';
import { getPlanResultFields } from '../../utils';
import { UNKNOWN_BAND_ID } from '../../config/constants';

export const getAssetsKeyedByBandId = (
  plannedTwinIDsKeyedByTwinId: Map<string, PlannedTwinID[]>,
  filteredAssetsForPlan: Map<string, Array<Asset>> | Array<Asset>,
) => {
  if (Array.isArray(filteredAssetsForPlan)) {
    const assetsKeyedByBandId = new Map<string, Array<Asset>>();
    assetsKeyedByBandId.set(UNKNOWN_BAND_ID, []);
    filteredAssetsForPlan.forEach((asset) => {
      const { destination } = asset;
      const [twinID] = destination?.twinID || [];
      const plannedTwinIDs = plannedTwinIDsKeyedByTwinId.get(twinID || '');
      if (!plannedTwinIDs) {
        assetsKeyedByBandId.get(UNKNOWN_BAND_ID)!.push(asset);
        return;
      }
      plannedTwinIDs?.forEach((plannedTwinID) => {
        const bandID = getPlanResultFields(plannedTwinID?.result).bandID || UNKNOWN_BAND_ID;
        if (!assetsKeyedByBandId.has(bandID)) {
          assetsKeyedByBandId.set(
            bandID,
            [],
          );
        }
        assetsKeyedByBandId.get(bandID)?.push(asset);
      });
    });

    return assetsKeyedByBandId;
  }

  const assetsKeyedByBandId = new Map<string, Array<Asset>>();
  filteredAssetsForPlan.forEach((assets, planID) => {
    assets.forEach((asset) => {
      const { destination } = asset;
      const [twinID] = destination?.twinID || [];
      plannedTwinIDsKeyedByTwinId.get(twinID || '')
        ?.filter(plannedTwinID => plannedTwinID.planID === planID)
        ?.forEach((plannedTwinID) => {
          const bandID = getPlanResultFields(plannedTwinID?.result).bandID || UNKNOWN_BAND_ID;
          if (!assetsKeyedByBandId.has(bandID)) {
            assetsKeyedByBandId.set(
              bandID,
              [],
            );
          }
          assetsKeyedByBandId.get(bandID)?.push(asset);
        });
    });
  });

  return assetsKeyedByBandId;
};

export const getFilteredAssetsKeyedByPlanId = createSelector(
  [
    getAssetManagerFilters,
    getFilteredAssetsContext,
    getAssetManagerSelectedAssetTypeId,
    getPlanConfigurationsKeyedById,
  ],
  (
    assetManagerFilters,
    filteredAssetsContext,
    selectedAssetTypeId,
    planConfigurationsKeyedById,
  ) => {
    const output = new Map<string, Array<Asset>>();

    const {
      searchTags,
      searchText,
      dateFilter,
      assetStatusFilter,
      observationTypeFilter,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      selectedLayerIds,
      fieldFilters,
      selectedBandIds,
    } = assetManagerFilters;
    if (
      !searchTags
      && !dateFilter?.length
      && !selectedAssetTypeId
      && !assetStatusFilter?.ids?.length
      && !observationTypeFilter?.size
    ) {
      planConfigurationsKeyedById.forEach((_, planId) => {
        output.set(planId, filteredAssetsContext.assetsArrayFromAssetType);
      });
      return output;
    }

    planConfigurationsKeyedById.forEach((_, planId) => {
      const bandsNotInPlan = omit(selectedBandIds, planId);
      const filteredAssets = getFilteredAssets(filteredAssetsContext, {
        searchTags,
        searchText,
        dateFilter,
        selectedAssetTypeId,
        assetStatusFilter,
        observationTypeFilter,
        tableFilter,
        openedLayerId,
        multiSearchModeFilter,
        selectedLayerIds,
        fieldFilters,
        selectedBandIds: bandsNotInPlan,
      });

      output.set(planId, filteredAssets);
    });

    return output;
  },
);

export const getFilteredAssetsKeyedByBandId = createSelector(
  [getPlannedTwinIDsKeyedByTwinId, getFilteredAssetsKeyedByPlanId],
  getAssetsKeyedByBandId,
);

export const getNumberOfAssetWithoutPlan = createSelector(
  [getFilteredAssetsKeyedByBandId],
  assetsKeyedByBandId => assetsKeyedByBandId?.get(UNKNOWN_BAND_ID)?.length ?? 0,
);
